import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styleSheets/recipientViewEdit.css";
import "../styleSheets/buttons.css";
import "../styleSheets/formFields.css";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChangeLogChangeType, ChangeLogEntityType, Recipient } from "../API";
import {
  createRecipient,
  deleteRecipient,
  updateRecipient,
} from "../graphql/mutations";
import { generateClient } from "aws-amplify/api";
import { useUser } from "../contexts/UserContext";
import {
  capitalizeFirst,
  formatPhoneToE164,
  getSortedAssociatedMessages,
  validateEmail,
  validatePhone,
  logToConsole,
} from "../utilities/utilities";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../graphql/queryKeys";
import { TooltipButton } from "../utilities/ToolTipButton";
import FormFieldRow from "./FormFieldRow";
import ConfirmationModal from "./ui/ConfirmationModal";
import BlockingPageLoader from "./ui/BlockingPageLoader";
import {
  useUnsavedChangesModalGuard,
  FieldErrorMap,
  trimFormValues,
  validateFormFields,
  requiredFieldValidator,
} from "../utilities/formValidation";
import { useNavigationGuard } from "../contexts/NavigationGuardContext";
import { useRecipients } from "../graphql/hooks";
import {
  type ChangeLogEntryInput,
  createChangeRequestId,
  logChanges,
} from "../graphql/fetchers";

type RecipientFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

const RECIPIENT_VALIDATORS = {
  firstName: [requiredFieldValidator("required")],
  lastName: [requiredFieldValidator("required")],
  email: [requiredFieldValidator("required")],
  phone: [requiredFieldValidator("required")],
};

type RecipientAuditSnapshot = {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
};

type RecipientChangedFields = {
  firstName?: { old: string | null; new: string | null };
  lastName?: { old: string | null; new: string | null };
  email?: { old: string | null; new: string | null };
  phone?: { old: string | null; new: string | null };
};

const EMPTY_RECIPIENT_AUDIT_SNAPSHOT: RecipientAuditSnapshot = {
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
};

const RECIPIENT_EDIT_CHANGE_LOG_METADATA = {
  source: "UI",
  page: "RecipientEdit",
  device: "web",
} as const;

const RECIPIENT_DELETE_CHANGE_LOG_METADATA = {
  source: "UI",
  action: "delete_confirmed",
} as const;

function normalizeRecipientAuditValue(
  value: string | null | undefined,
): string | null {
  const trimmedValue = (value ?? "").trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
}

function normalizeRecipientAuditEmail(
  value: string | null | undefined,
): string | null {
  const normalizedValue = normalizeRecipientAuditValue(value);
  return normalizedValue ? normalizedValue.toLowerCase() : null;
}

function normalizeRecipientAuditPhone(
  value: string | null | undefined,
): string | null {
  const normalizedValue = normalizeRecipientAuditValue(value);
  if (!normalizedValue) return null;

  const formattedPhone = formatPhoneToE164(normalizedValue);
  return formattedPhone ?? normalizedValue;
}

function getRecipientSnapshot(
  recipient: Pick<
    Recipient,
    "firstName" | "lastName" | "email" | "phone"
  > | null,
): RecipientAuditSnapshot {
  if (!recipient) {
    return EMPTY_RECIPIENT_AUDIT_SNAPSHOT;
  }

  return {
    firstName: normalizeRecipientAuditValue(recipient.firstName),
    lastName: normalizeRecipientAuditValue(recipient.lastName),
    email: normalizeRecipientAuditEmail(recipient.email),
    phone: normalizeRecipientAuditPhone(recipient.phone),
  };
}

function getChangedFields(
  before: RecipientAuditSnapshot,
  after: RecipientAuditSnapshot,
): RecipientChangedFields {
  const changedFields: RecipientChangedFields = {};

  if (before.firstName !== after.firstName) {
    changedFields.firstName = { old: before.firstName, new: after.firstName };
  }

  if (before.lastName !== after.lastName) {
    changedFields.lastName = { old: before.lastName, new: after.lastName };
  }

  if (before.email !== after.email) {
    changedFields.email = { old: before.email, new: after.email };
  }

  if (before.phone !== after.phone) {
    changedFields.phone = { old: before.phone, new: after.phone };
  }

  return changedFields;
}

function getOpenedRecipientSnapshot(
  sourceRecipient: Recipient | null,
  fetchedRecipient: Recipient | null,
): RecipientAuditSnapshot {
  return getRecipientSnapshot(sourceRecipient ?? fetchedRecipient);
}

function buildSavedRecipientSnapshot(
  savedRecipient: Partial<Recipient> & { id: string },
  previousRecipient: Recipient | null,
  userId: string,
): Recipient {
  const timestamp = new Date().toISOString();

  return {
    __typename: "Recipient",
    id: savedRecipient.id,
    firstName: savedRecipient.firstName ?? null,
    lastName: savedRecipient.lastName ?? null,
    email: savedRecipient.email ?? null,
    phone: savedRecipient.phone ?? null,
    phoneType: savedRecipient.phoneType ?? previousRecipient?.phoneType ?? null,
    sendSMS: savedRecipient.sendSMS ?? previousRecipient?.sendSMS ?? null,
    userprofileID: savedRecipient.userprofileID ?? userId,
    messages: previousRecipient?.messages ?? savedRecipient.messages ?? null,
    createdAt:
      savedRecipient.createdAt ?? previousRecipient?.createdAt ?? timestamp,
    updatedAt: savedRecipient.updatedAt ?? timestamp,
  };
}

function getRecipientFormValues(
  recipient: Pick<
    Recipient,
    "firstName" | "lastName" | "email" | "phone"
  > | null,
): RecipientFormValues {
  return {
    firstName: recipient?.firstName ?? "",
    lastName: recipient?.lastName ?? "",
    email: recipient?.email ?? "",
    phone: recipient?.phone ?? "",
  };
}

function RecipientEdit() {
  const navigate = useNavigate();
  const { setGuard } = useNavigationGuard();
  const location = useLocation();
  const { recipient: routeRecipientId } = useParams<{ recipient?: string }>();
  const { t } = useTranslation("common");
  const { user, isAuthenticated } = useUser();
  const queryClient = useQueryClient();

  const userId = user?.userId ?? "";
  const recipientsQ = useRecipients(userId, isAuthenticated && !!userId);
  const stateRecipient = (location.state as Recipient) || null;
  const recipientId = (routeRecipientId ?? stateRecipient?.id ?? "").trim();
  const sourceRecipient =
    !recipientId || stateRecipient?.id === recipientId ? stateRecipient : null;
  const isNew = !recipientId;
  const fetchedRecipient = useMemo(
    () =>
      (recipientsQ.data ?? []).find((item) => item.id === recipientId) ?? null,
    [recipientId, recipientsQ.data],
  );
  const [currentRecipient, setCurrentRecipient] = useState<Recipient | null>(
    sourceRecipient,
  );
  const recipient = currentRecipient ?? sourceRecipient;
  const auditBaselineSnapshotRef = useRef<RecipientAuditSnapshot>(
    isNew
      ? EMPTY_RECIPIENT_AUDIT_SNAPSHOT
      : getOpenedRecipientSnapshot(sourceRecipient, fetchedRecipient),
  );

  const [isSaveResultModalOpen, setIsSaveResultModalOpen] = useState(false);
  const [saveResultTitle, setSaveResultTitle] = useState("");
  const [saveResultMessage, setSaveResultMessage] = useState("");
  const [saveShouldReturnToPanel, setSaveShouldReturnToPanel] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [formValues, setFormValues] = useState<RecipientFormValues>(
    getRecipientFormValues(recipient),
  );
  const [initialValues, setInitialValues] = useState<RecipientFormValues>(
    getRecipientFormValues(recipient),
  );
  const [fieldErrors, setFieldErrors] = useState<
    FieldErrorMap<RecipientFormValues>
  >({});
  const [formError, setFormError] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isLoadingExistingRecipient =
    !isNew && !recipient && recipientsQ.isLoading;

  const associatedMessages = useMemo(
    () => getSortedAssociatedMessages(recipient),
    [recipient],
  );

  useEffect(() => {
    if (isNew) {
      setCurrentRecipient(null);
      return;
    }

    setCurrentRecipient(sourceRecipient);
  }, [isNew, recipientId, sourceRecipient]);

  useEffect(() => {
    if (!isNew && !currentRecipient && fetchedRecipient) {
      setCurrentRecipient(fetchedRecipient);
    }
  }, [currentRecipient, fetchedRecipient, isNew]);

  useEffect(() => {
    auditBaselineSnapshotRef.current = isNew
      ? EMPTY_RECIPIENT_AUDIT_SNAPSHOT
      : getOpenedRecipientSnapshot(sourceRecipient, fetchedRecipient);
  }, [fetchedRecipient, isNew, sourceRecipient]);

  useEffect(() => {
    const next = {
      firstName: recipient?.firstName ?? "",
      lastName: recipient?.lastName ?? "",
      email: recipient?.email ?? "",
      phone: recipient?.phone ?? "",
    };
    setFormValues(next);
    setInitialValues(next);
    setFieldErrors({});
    setFormError("");
  }, [
    recipient?.email,
    recipient?.firstName,
    recipient?.id,
    recipient?.lastName,
    recipient?.phone,
  ]);

  const normalizedValues = useMemo(
    () => trimFormValues(formValues),
    [formValues],
  );
  const normalizedInitialValues = useMemo(
    () => trimFormValues(initialValues),
    [initialValues],
  );
  const hasUnsavedChanges = useMemo(
    () =>
      JSON.stringify(normalizedValues) !==
      JSON.stringify(normalizedInitialValues),
    [normalizedInitialValues, normalizedValues],
  );

  const {
    isPromptOpen,
    requestNavigation,
    confirmNavigation,
    cancelNavigation,
  } = useUnsavedChangesModalGuard(hasUnsavedChanges);
  useEffect(() => {
    setGuard(hasUnsavedChanges ? requestNavigation : null);
    return () => setGuard(null);
  }, [hasUnsavedChanges, requestNavigation, setGuard]);

  const handleDeleteRecipient = () => {
    if (associatedMessages.length > 0 || isNew || !recipient?.id) {
      return;
    }
    setFormError("");
    setIsDeleteModalOpen(true);
  };

  const handleKeepRecipient = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDiscardChanges = () => {
    if (hasUnsavedChanges && !isNew && recipient?.id) {
      void logChanges([
        {
          requestId: createChangeRequestId(),
          entityType: ChangeLogEntityType.RECIPIENT,
          entityId: recipient.id,
          changeType: ChangeLogChangeType.ABANDON,
          metadata: RECIPIENT_EDIT_CHANGE_LOG_METADATA,
          userId,
        },
      ]);
    }

    confirmNavigation();
  };

  const handleConfirmDeleteRecipient = async () => {
    if (
      isDeleting ||
      associatedMessages.length > 0 ||
      isNew ||
      !recipient?.id ||
      !userId
    ) {
      return;
    }

    setIsDeleting(true);
    setFormError("");

    const client = generateClient();
    try {
      await client.graphql({
        query: deleteRecipient,
        variables: { input: { id: recipient.id } },
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.recipients(userId),
        }),
      ]);
      void logChanges([
        {
          requestId: createChangeRequestId(),
          entityType: ChangeLogEntityType.RECIPIENT,
          entityId: recipient.id,
          changeType: ChangeLogChangeType.DELETE,
          metadata: RECIPIENT_DELETE_CHANGE_LOG_METADATA,
          userId,
        },
      ]);
      setIsDeleteModalOpen(false);
      navigate("/Panel?selectedTab=recipients");
    } catch (error) {
      logToConsole("error", "Error deleting recipient:", error);
      setFormError(t("recipient.errorMessage.recipientDeleteError"));
      setIsDeleteModalOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const setFieldValue = (field: keyof RecipientFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    setFormError("");
  };

  const validateRecipientForm = () => {
    const errors = validateFormFields(normalizedValues, RECIPIENT_VALIDATORS);

    if (errors.firstName)
      errors.firstName = t("recipient.validation.firstNameRequired");
    if (errors.lastName)
      errors.lastName = t("recipient.validation.lastNameRequired");
    if (errors.email) errors.email = t("recipient.validation.emailRequired");
    if (errors.phone) errors.phone = t("recipient.validation.phoneRequired");

    if (!errors.email && !validateEmail(normalizedValues.email.toLowerCase())) {
      errors.email = t("recipient.validation.emailInvalid");
    }

    if (!errors.phone && !validatePhone(normalizedValues.phone)) {
      errors.phone = t("recipient.validation.phoneInvalid");
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError(t("recipient.validation.formErrors"));
      return false;
    }
    return true;
  };

  const handleSaveRecipient = async () => {
    if (!isNew && !recipient?.id) {
      setFormError(t("errorMessage.failedLoad"));
      return;
    }

    if (!hasUnsavedChanges) {
      setSaveShouldReturnToPanel(true);
      setSaveResultTitle(t("errorMessage.noChangesTitle"));
      setSaveResultMessage(t("errorMessage.noChangesToSave"));
      setIsSaveResultModalOpen(true);
      return;
    }

    if (!validateRecipientForm()) return;

    const normalizedEmail = normalizedValues.email.toLowerCase();
    const formattedPhone = formatPhoneToE164(normalizedValues.phone);

    if (!formattedPhone) {
      setFieldErrors((prev) => ({
        ...prev,
        phone: t("recipient.validation.phoneInvalid"),
      }));
      setFormError(t("recipient.validation.formErrors"));
      return;
    }

    const client = generateClient();
    let nextInitialValues = normalizedValues;
    setIsSaving(true);

    try {
      if (!isNew) {
        const beforeAuditSnapshot = auditBaselineSnapshotRef.current;
        const input = {
          id: recipient!.id,
          firstName: normalizedValues.firstName,
          lastName: normalizedValues.lastName,
          email: normalizedEmail,
          phone: formattedPhone,
          userprofileID: userId,
        };

        const result: any = await client.graphql({
          query: updateRecipient,
          variables: { input },
        });
        const updatedRecipient = result?.data?.updateRecipient;

        logToConsole("log", "Updated recipient:", updatedRecipient);

        if (updatedRecipient?.id) {
          const savedRecipient = buildSavedRecipientSnapshot(
            updatedRecipient,
            recipient,
            userId,
          );
          const afterAuditSnapshot = getRecipientSnapshot(savedRecipient);
          nextInitialValues = getRecipientFormValues(savedRecipient);
          setCurrentRecipient(savedRecipient);
          auditBaselineSnapshotRef.current = afterAuditSnapshot;
          const updateRequestId = createChangeRequestId();
          const updateAuditEntries: ChangeLogEntryInput[] = [
            {
              requestId: updateRequestId,
              entityType: ChangeLogEntityType.RECIPIENT,
              entityId: updatedRecipient.id,
              changeType: ChangeLogChangeType.UPDATE,
              metadata: RECIPIENT_EDIT_CHANGE_LOG_METADATA,
              userId,
            },
          ];
          const changedFields = getChangedFields(
            beforeAuditSnapshot,
            afterAuditSnapshot,
          );

          if (changedFields.firstName) {
            updateAuditEntries.push({
              requestId: updateRequestId,
              entityType: ChangeLogEntityType.RECIPIENT,
              entityId: updatedRecipient.id,
              changeType: ChangeLogChangeType.UPDATE,
              fieldName: "firstName",
              oldValue: changedFields.firstName.old,
              newValue: changedFields.firstName.new,
              metadata: RECIPIENT_EDIT_CHANGE_LOG_METADATA,
              userId,
            });
          }

          if (changedFields.lastName) {
            updateAuditEntries.push({
              requestId: updateRequestId,
              entityType: ChangeLogEntityType.RECIPIENT,
              entityId: updatedRecipient.id,
              changeType: ChangeLogChangeType.UPDATE,
              fieldName: "lastName",
              oldValue: changedFields.lastName.old,
              newValue: changedFields.lastName.new,
              metadata: RECIPIENT_EDIT_CHANGE_LOG_METADATA,
              userId,
            });
          }

          if (changedFields.email) {
            updateAuditEntries.push({
              requestId: updateRequestId,
              entityType: ChangeLogEntityType.RECIPIENT,
              entityId: updatedRecipient.id,
              changeType: ChangeLogChangeType.UPDATE,
              fieldName: "email",
              oldValue: changedFields.email.old,
              newValue: changedFields.email.new,
              metadata: RECIPIENT_EDIT_CHANGE_LOG_METADATA,
              userId,
            });
          }

          if (changedFields.phone) {
            updateAuditEntries.push({
              requestId: updateRequestId,
              entityType: ChangeLogEntityType.RECIPIENT,
              entityId: updatedRecipient.id,
              changeType: ChangeLogChangeType.UPDATE,
              fieldName: "phone",
              oldValue: changedFields.phone.old,
              newValue: changedFields.phone.new,
              metadata: RECIPIENT_EDIT_CHANGE_LOG_METADATA,
              userId,
            });
          }

          void logChanges(updateAuditEntries);
          if (routeRecipientId !== updatedRecipient.id) {
            navigate(`/RecipientEdit/${updatedRecipient.id}`, {
              replace: true,
              state: savedRecipient,
            });
          }
        }
      } else {
        const input = {
          firstName: normalizedValues.firstName,
          lastName: normalizedValues.lastName,
          email: normalizedEmail,
          phone: formattedPhone,
          userprofileID: userId,
        };

        const result: any = await client.graphql({
          query: createRecipient,
          variables: { input },
        });
        const createdRecipient = result?.data?.createRecipient;

        logToConsole("log", "New Recipient:", createdRecipient);

        if (createdRecipient?.id) {
          const savedRecipient = buildSavedRecipientSnapshot(
            createdRecipient,
            null,
            userId,
          );
          nextInitialValues = getRecipientFormValues(savedRecipient);
          auditBaselineSnapshotRef.current =
            getRecipientSnapshot(savedRecipient);
          setCurrentRecipient(savedRecipient);
          void logChanges([
            {
              requestId: createChangeRequestId(),
              entityType: ChangeLogEntityType.RECIPIENT,
              entityId: createdRecipient.id,
              changeType: ChangeLogChangeType.CREATE,
              metadata: RECIPIENT_EDIT_CHANGE_LOG_METADATA,
              userId,
            },
          ]);
          navigate(`/RecipientEdit/${createdRecipient.id}`, {
            replace: true,
            state: savedRecipient,
          });
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.recipients(userId),
        }),
      ]);
      setInitialValues(nextInitialValues);
      setFieldErrors({});
      setFormError("");
      setSaveShouldReturnToPanel(true);
      setSaveResultTitle(t("common.saveWindowTitle"));
      setSaveResultMessage(t("recipient.onScreenMessage.saveRecipientSuccess"));
      setIsSaveResultModalOpen(true);
    } catch (error) {
      logToConsole("error", "Error saving recipient:", error);
      setFormError(t("recipient.errorMessage.recipientSaveError"));
      setSaveShouldReturnToPanel(false);
      setSaveResultTitle(t("common.saveWindowTitle"));
      setSaveResultMessage(t("recipient.errorMessage.recipientSaveError"));
      setIsSaveResultModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    requestNavigation(() => navigate("/Panel?selectedTab=recipients"));
  };
  const handleContinueEditingAfterSave = () => {
    setIsSaveResultModalOpen(false);
  };

  const handleCloseAfterSave = () => {
    setIsSaveResultModalOpen(false);
    if (saveShouldReturnToPanel) {
      navigate("/Panel?selectedTab=recipients");
    }
  };
  const canDeleteRecipient =
    !isNew && !!recipient?.id && associatedMessages.length === 0 && !isSaving;

  if (!isNew && !recipient && !recipientsQ.isLoading) {
    return <Navigate to="/Panel?selectedTab=recipients" replace />;
  }

  if (isSaving) {
    return <BlockingPageLoader message={t("common.saving")} />;
  }

  return (
    <div className="recipient-edit-page">
      <div className="recipient-topbar">
        <div className="recipient-topbar__nav">
          <div className="recipient-topbar__container">
            <button
              id="cancel-recipient-btn"
              type="button"
              className="btn btn--gray btn--size-md btn--icon-left"
              onClick={handleCancel}
            >
              <div className="btn__icon icon-chevron-left" aria-hidden="true" />
              {t("common.cancel")}
            </button>

            <div className="recipient-topbar__title">
              {capitalizeFirst(t("recipient.recipient"))}
            </div>

            <div className="recipient-topbar__actions">
              <button
                id="save-recipient-btn"
                type="button"
                className="btn btn--green btn--size-md btn--icon-left"
                onClick={handleSaveRecipient}
                disabled={isLoadingExistingRecipient || isSaving}
              >
                <span className="btn__icon icon-check" aria-hidden="true" />
                {t("common.save")}
              </button>
            </div>
          </div>

          <div className="recipient-topbar__titleBelow">
            {capitalizeFirst(t("recipient.recipient"))}
          </div>
        </div>
      </div>

      <div className="recipient-content">
        {isLoadingExistingRecipient ? (
          <div className="recipient-formError">{t("common.loading")}</div>
        ) : null}
        {formError ? (
          <div className="recipient-formError">{formError}</div>
        ) : null}

        <div className="recipient-formCard">
          <FormFieldRow
            id="recipient-firstName-input"
            label={t("recipient.firstName")}
            required
            error={fieldErrors.firstName}
            isWritten={
              normalizedValues.firstName.length > 0 && !fieldErrors.firstName
            }
          >
            <input
              id="recipient-firstName-input"
              className="recipient-input"
              type="text"
              value={formValues.firstName}
              autoComplete="given-name"
              disabled={isLoadingExistingRecipient || isSaving}
              onChange={(e) => setFieldValue("firstName", e.target.value)}
            />
          </FormFieldRow>

          <FormFieldRow
            id="recipient-lastName-input"
            label={t("recipient.lastName")}
            required
            error={fieldErrors.lastName}
            isWritten={
              normalizedValues.lastName.length > 0 && !fieldErrors.lastName
            }
          >
            <input
              id="recipient-lastName-input"
              className="recipient-input"
              type="text"
              value={formValues.lastName}
              autoComplete="family-name"
              disabled={isLoadingExistingRecipient || isSaving}
              onChange={(e) => setFieldValue("lastName", e.target.value)}
            />
          </FormFieldRow>

          <FormFieldRow
            id="recipient-email-input"
            label={t("recipient.email")}
            required
            error={fieldErrors.email}
            isWritten={normalizedValues.email.length > 0 && !fieldErrors.email}
          >
            <input
              id="recipient-email-input"
              className="recipient-input"
              type="text"
              value={formValues.email}
              autoComplete="email"
              disabled={isLoadingExistingRecipient || isSaving}
              onChange={(e) => setFieldValue("email", e.target.value)}
            />
          </FormFieldRow>

          <FormFieldRow
            id="recipient-phone-input"
            label={t("recipient.phone")}
            required
            error={fieldErrors.phone}
            isWritten={normalizedValues.phone.length > 0 && !fieldErrors.phone}
          >
            <input
              id="recipient-phone-input"
              className="recipient-input"
              type="text"
              value={formValues.phone}
              autoComplete="tel"
              disabled={isLoadingExistingRecipient || isSaving}
              onChange={(e) => setFieldValue("phone", e.target.value)}
            />
          </FormFieldRow>
        </div>

        <div className="delete-wrapper">
          <TooltipButton
            label={t("recipient.deleteRecipient")}
            id="delete-recipient-btn"
            tooltip={t("recipient.onScreenMessage.deleteRecipientBlocked")}
            disabled={!canDeleteRecipient}
            onClick={handleDeleteRecipient}
            buttonClassName="btn btn--transparent-no-move recipient-deleteBtn"
            buttonIcon={true}
            buttonIconClassName="icon-trash"
          />
        </div>

        <div className="recipient-detailsCard">
          <div className="recipient-detailsCard__title">
            {t("common.associatedMessages")}
          </div>

          <div className="recipient-associated">
            {associatedMessages.length === 0 ? (
              <div className="recipient-associated__item recipient-associated__item--empty">
                {t("common.noAssociatedMessages")}
              </div>
            ) : (
              associatedMessages.map((m: any) => (
                <div key={m.message.id} className="recipient-associated__item">
                  {m.message.title ?? t("message.untitledMessage")}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isPromptOpen}
        title={t("errorMessage.unsavedChanges")}
        message={t("errorMessage.unsavedChangesPrompt")}
        primaryLabel={t("common.discardChanges")}
        secondaryLabel={t("common.continueEdit")}
        onPrimary={handleConfirmDiscardChanges}
        onSecondary={cancelNavigation}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
      />
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        title={t("recipient.deleteRecipient")}
        message={t("recipient.onScreenMessage.deleteRecipientConfirm")}
        primaryLabel={isDeleting ? t("common.deleting") : t("common.delete")}
        secondaryLabel={t("common.keep")}
        onPrimary={handleConfirmDeleteRecipient}
        onSecondary={handleKeepRecipient}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
        primaryDisabled={isDeleting}
        secondaryDisabled={isDeleting}
      />
      <ConfirmationModal
        isOpen={isSaveResultModalOpen}
        title={saveResultTitle}
        message={saveResultMessage}
        primaryLabel={
          saveShouldReturnToPanel ? t("common.finishEdit") : t("common.close")
        }
        secondaryLabel={t("common.continueEdit")}
        onPrimary={handleCloseAfterSave}
        onSecondary={handleContinueEditingAfterSave}
        primaryButtonClassName="btn btn--orange btn--size-md"
        secondaryButtonClassName="btn btn--white btn--size-md"
      />
    </div>
  );
}

export default RecipientEdit;
